import React, { useState, useEffect, useRef } from 'react';
import { Modal } from "react-bootstrap";
import '../beneficiary.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import Button from '@components/common/Button';
import { encodeWithKeyAndShift, decodeWithKeyAndShift } from '@utils/base64Utils';
import { postDataJSON } from '@services/CommonApiService';
import endpoints from '@config/endpoints';
import { errorLogger } from '@utils/applicationLogUtils';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@store/reducers';
import { toast } from 'react-toastify';
import { setBeneficiaryData, setTsnID } from '@store/actions/beneficiaryAction';
import { GENDER } from '@constants/index';
import { benef_const } from '@constants/beneficiaryConstant';

interface FaceAuthenticationModalProps {
    showModal: boolean;
    handleClose: (type: string, beneficiaryData?: any) => void;
    skipGenderCheck?: boolean;
}
const extractErrorMessage = (error: any): string => {
    try {
        const messages = error?.response?.data?.messages;
        if (messages && Array.isArray(messages) && messages.length > 0) {
            const firstMessage = messages[0];
            if (firstMessage.stackTraces) {
                const stackTrace = firstMessage.stackTraces;
                
                if (stackTrace.includes('[object Object]')) {
                    const beforeObjectMatch = stackTrace.match(/Error:\s*(.+?)\s*,\s*Details:/);
                    if (beforeObjectMatch) {
                        return beforeObjectMatch[1].trim();
                    }
                    return 'Unable to process the request. Please try again or use an alternate verification method.';
                }
                
                if (stackTrace.includes('UIDAI Error')) {
                    const uidaiMatch = stackTrace.match(/UIDAI Error code : (\d+) : (.+?)(?:$)/);
                    if (uidaiMatch) {
                        const errorCode = uidaiMatch[1];
                        const errorMessage = uidaiMatch[2].trim();                        
                        if (errorCode === '330' && errorMessage.includes('locked biometrics')) {
                            return 'User has locked biometrics';
                        }                        
                        return errorMessage.replace(/\.$/, '');
                    }
                }
                
                const detailsMatch = stackTrace.match(/Details: (.+?)(?:\.|$)/);
                if (detailsMatch) {
                    const detailText = detailsMatch[1].trim();
                    if (detailText !== '[object Object]') {
                        try {
                            const detailsJson = JSON.parse(detailText);
                            if (detailsJson.aadhaarNumber) {
                                return detailsJson.aadhaarNumber;
                            }
                        } catch (jsonError) {
                            errorLogger(jsonError, "Error parsing details JSON in extractErrorMessage");
                        }
                        return detailText;
                    }
                }
                
                if (!stackTrace.includes('[object Object]')) {
                    return stackTrace;
                }
            }
            
            if (firstMessage.messageDetails && firstMessage.messageDetails !== 'Internal server error (e.g., unhandled exception)') {
                return firstMessage.messageDetails;
            }
            
            if (firstMessage.messageDescription && firstMessage.messageDescription !== 'InternalServerError') {
                return firstMessage.messageDescription;
            }
        }
        
        return error?.response?.data?.message || error?.message || 'An unexpected error occurred';
    } catch (e) {
        return 'An unexpected error occurred';
    }
};
const FaceAuthenticationModal: React.FC<FaceAuthenticationModalProps> = ({ showModal, handleClose, skipGenderCheck = false }) => {
    const dispatch = useDispatch();
    const beneficiary = useSelector((state: RootState) => state.beneficiary);
    const [qrCodeData, setQrCodeData] = useState<string | null>(null);
    const [timeLeft, setTimeLeft] = useState(600);
    const [isGenerating, setIsGenerating] = useState(false);
    const [scanStatus, setScanStatus] = useState<string>('');
    const [qrId, setQrId] = useState<string>('');
    const [txnId, setTxnId] = useState<string>('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [verifiedBeneficiaryData, setVerifiedBeneficiaryData] = useState<any>(null);
    const [retryCount, setRetryCount] = useState<number>(0);
    const [showMaxAttemptsWarning, setShowMaxAttemptsWarning] = useState<boolean>(false);
    const [mobileLinkedError, setMobileLinkedError] = useState<{ code: string; message: string } | null>(null);
    const [invalidAadhaarError, setInvalidAadhaarError] = useState<string | null>(null);
    const [biometricMismatchError, setBiometricMismatchError] = useState<{ code: string; message: string } | null>(null);
    const [invalidGenderMessage, setInvalidGenderMessage] = useState<string>('');
    const isPollingActiveRef = useRef<boolean>(false);
    const scanStatusIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!showModal) {
            isPollingActiveRef.current = false;
            if (scanStatusIntervalRef.current) {
                clearInterval(scanStatusIntervalRef.current);
                scanStatusIntervalRef.current = null;
            }
            setQrCodeData(null);
            setTimeLeft(600);
            setIsGenerating(false);
            setScanStatus('');
            setQrId('');
            setTxnId('');
            setVerifiedBeneficiaryData(null);
            setRetryCount(0);
            setShowMaxAttemptsWarning(false);
            setMobileLinkedError(null);
            setInvalidAadhaarError(null);
            setBiometricMismatchError(null);
            setInvalidGenderMessage('');
        }
    }, [showModal]);

    useEffect(() => {
        let countdownInterval: NodeJS.Timeout | null = null;

        if (timeLeft > 0 && qrCodeData && showModal) {
            isPollingActiveRef.current = true;
            countdownInterval = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        setQrCodeData(null);
                        setScanStatus('FAILED');
                        setQrId('');
                        isPollingActiveRef.current = false;
                        
                        if (scanStatusIntervalRef.current) {
                            clearInterval(scanStatusIntervalRef.current);
                            scanStatusIntervalRef.current = null;
                        }
                        
                        setRetryCount((prevRetryCount) => {
                            const newRetryCount = prevRetryCount + 1;
                            
                            if (newRetryCount >= 3) {
                                setShowMaxAttemptsWarning(true);
                                toast.error('QR Code expired. Maximum attempts reached. Please try alternate verification method.', {
                                    position: "top-center",
                                    autoClose: 5000,
                                });
                            } else {
                                toast.error(`QR Code expired. You have ${3 - newRetryCount} attempt(s) remaining.`, {
                                    position: "top-center",
                                });
                            }
                            
                            return newRetryCount;
                        });
                        
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else if (!showModal) {
            isPollingActiveRef.current = false;
        }

        return () => {
            if (countdownInterval) clearInterval(countdownInterval);
        };
    }, [timeLeft, qrCodeData, showModal]);

    // COMMENTED OUT: Face authentication verify flow - Only creation flow is active
    // const verifyFaceAuthentication = async (txnId: string, faceAuthPid: string) => {
    //     try {
    //         setIsVerifying(true);            
    //         const verifyPayload = {
    //             txnId: txnId,
    //             faceAuthPid: faceAuthPid
    //         };

    //         const verifyResponse = await postDataJSON<any>(
    //             `${endpoints.faceAuthVerify}`,
    //             verifyPayload
    //         );
    //         if (verifyResponse.data && verifyResponse.data.ABHANumber) {
    //             toast.success('Face authentication successful!', {
    //                 position: "top-center",
    //             });

    //             const profile = verifyResponse.data;
                
    //             const rawAbhaNumber = profile.ABHANumber;
    //             const cleanAbhaNumber = rawAbhaNumber ? rawAbhaNumber.replace(/-/g, '') : '';
                
    //             const day = profile.dayOfBirth ? profile.dayOfBirth.toString().padStart(2, '0') : '';
    //             const month = profile.monthOfBirth ? profile.monthOfBirth.toString().padStart(2, '0') : '';
    //             const year = profile.yearOfBirth || '';
    //             const formattedDob = (day && month && year) ? `${day}-${month}-${year}` : '';
                
    //             const beneficiaryDetails = {
    //                 abhaId: cleanAbhaNumber,
    //                 address: profile.preferredAbhaAddress || '',
    //                 name: profile.name || `${profile.firstName || ''} ${profile.middleName || ''} ${profile.lastName || ''}`.trim(),
    //                 profile: profile.profilePhoto || '',
    //                 mob: profile.mobile || '',
    //                 dob: formattedDob
    //             };
                
    //             setVerifiedBeneficiaryData(beneficiaryDetails);                    
    //             dispatch(setBeneficiaryData([beneficiaryDetails]));
    //             sessionStorage.removeItem('faceAuthDeepLink');
    //             sessionStorage.removeItem('faceAuthBeneficiaryIndex');
    //             sessionStorage.removeItem('faceAuthSearchTxnId');

    //             setTimeout(() => {
    //                 setIsVerifying(false);
    //                 handleClose('submit', beneficiaryDetails);
    //             }, 1500);
    //         } else {
    //             toast.error('Face authentication verification failed', {
    //                 position: "top-center",
    //             });
    //             setIsVerifying(false);
    //             setScanStatus('FAILED');
    //         }
    //     } catch (error: any) {
    //         const errorMessage = extractErrorMessage(error) || 'Failed to verify face authentication. Please try again.';
            
    //         const newRetryCount = retryCount + 1;
    //         setRetryCount(newRetryCount);
            
    //         if (newRetryCount >= 3) {
    //             setShowMaxAttemptsWarning(true);
    //             toast.error(`${errorMessage} Maximum attempts reached. Please try alternate verification method.`, {
    //                 position: "top-center",
    //                 autoClose: 5000,
    //             });
    //         } else {
    //             toast.error(`${errorMessage} You have ${3 - newRetryCount} attempt(s) remaining.`, {
    //                 position: "top-center",
    //             });
    //         }
            
    //         errorLogger(error?.response?.data || error?.response || error);
    //         setIsVerifying(false);
    //         setScanStatus('FAILED');
    //     }
    // };

    const enrollAbhaByFaceAuth = async (txnId: string, apiResponseTrxnId: string) => {
        try {
            setIsVerifying(true);
            const aadhaarNumber = beneficiary?.aadhar;
            const mobileNumber = beneficiary?.beneficiaryMobileNumber;
            if (!aadhaarNumber || !mobileNumber) {
                toast.error('Aadhaar and Mobile number are required', {
                    position: "top-center",
                });
                setIsVerifying(false);
                setScanStatus('FAILED');
                return;
            }

            const enrollPayload = {
                aadhaar: aadhaarNumber,
                mobile: mobileNumber,
                apiResponseTrxnId: apiResponseTrxnId
            };

            const enrollResponse = await postDataJSON<any>(
                `${endpoints.faceAuthEnrolByAadhaar}`,
                enrollPayload
            );

            if (enrollResponse.data && enrollResponse.data.ABHAProfile) {
                const profile = enrollResponse.data.ABHAProfile;
                const profileGender = profile.gender;
                
                const isChildRegistration = beneficiary?.isChildRegistration;
                if (!skipGenderCheck && !isChildRegistration && beneficiary?.beneficiaryGender === GENDER.FEMALE && profileGender !== GENDER.female) {
                    setInvalidGenderMessage(benef_const.genderMisMatchFemale);
                    setIsVerifying(false);
                    setScanStatus('FAILED');
                    return;
                }
                else if (!skipGenderCheck && !isChildRegistration && beneficiary?.beneficiaryGender === GENDER.MALE && profileGender !== GENDER.male) {
                    setInvalidGenderMessage(benef_const.genderMisMatchMale);
                    setIsVerifying(false);
                    setScanStatus('FAILED');
                    return;
                }

                toast.success(enrollResponse.data.message || 'ABHA created successfully!', {
                    position: "top-center",
                });

                const fullName = [profile.firstName, profile.middleName, profile.lastName]
                    .filter(name => name)
                    .join(' ');
                const cleanAbhaNumber = profile.ABHANumber ? profile.ABHANumber.replace(/-/g, '') : '';
                
                const beneficiaryDetails = {
                    abhaId: cleanAbhaNumber,
                    address: profile.phrAddress && profile.phrAddress.length > 0 ? profile.phrAddress[0] : '',
                    name: fullName,
                    profile: profile.photo || '',
                    dob: profile.dob || '',
                    mob: mobileNumber || '',
                    gender: profileGender
                };
                
                setVerifiedBeneficiaryData(beneficiaryDetails);                    
                dispatch(setBeneficiaryData([beneficiaryDetails]));

                sessionStorage.removeItem('faceAuthDeepLink');
                sessionStorage.removeItem('faceAuthBeneficiaryIndex');
                sessionStorage.removeItem('faceAuthSearchTxnId');

                setTimeout(() => {
                    setIsVerifying(false);
                    handleClose('submit', beneficiaryDetails);
                }, 1500);
            } else {
                toast.error('ABHA creation failed', {
                    position: "top-center",
                });
                setIsVerifying(false);
                setScanStatus('FAILED');
            }
        } catch (error: any) {
            const abdmError = error?.response?.data?.error;
            if (abdmError && abdmError.code === 'ABDM-1204') {
                setBiometricMismatchError({
                    code: abdmError.code,
                    message: abdmError.message
                });
                
                const biometricMessage = abdmError.message.includes('Biometric data did not match') 
                    ? 'Biometric data did not match.'
                    : abdmError.message;
                
                const newRetryCount = retryCount + 1;
                setRetryCount(newRetryCount);
                
                if (newRetryCount >= 3) {
                    setShowMaxAttemptsWarning(true);
                    toast.error(`${biometricMessage} Maximum attempts reached. Please try alternate verification method.`, {
                        position: "top-center",
                        autoClose: 5000,
                    });
                } else {
                    toast.error(`${biometricMessage} You have ${3 - newRetryCount} attempt(s) remaining.`, {
                        position: "top-center",
                    });
                }
                
                errorLogger(error?.response?.data || error, "Face Auth Enrol - ABDM-1204");
                setIsVerifying(false);
                setScanStatus('FAILED');
                setQrCodeData(null);
                return;
            }
            
            if (abdmError && abdmError.code === 'ABDM-1124') {
                setMobileLinkedError({
                    code: abdmError.code,
                    message: abdmError.message
                });
                toast.error(`${abdmError.code}: ${abdmError.message}`, {
                    position: "top-center",
                    autoClose: 5000,
                });
                errorLogger(error?.response?.data || error, "Face Auth Enrol - ABDM-1124");
                setIsVerifying(false);
                setScanStatus('FAILED');
                setQrCodeData(null);
                return;
            }
            
            const errorMessage = extractErrorMessage(error) || 'Failed to create ABHA. Please try again.';
            
            if (errorMessage.toLowerCase().includes('invalid aadhaar number') || errorMessage.toLowerCase().includes('invalid aadhar')) {
                setInvalidAadhaarError(errorMessage);
                const newRetryCount = retryCount + 1;
                setRetryCount(newRetryCount);
                
                if (newRetryCount >= 3) {
                    setShowMaxAttemptsWarning(true);
                    toast.error(`${errorMessage}. Maximum attempts reached. Please verify Aadhaar number and try again.`, {
                        position: "top-center",
                        autoClose: 5000,
                    });
                } else {
                    toast.error(`${errorMessage}. You have ${3 - newRetryCount} attempt(s) remaining.`, {
                        position: "top-center",
                    });
                }
                
                errorLogger(error?.response?.data || error, "Face Auth Enrol - Invalid Aadhaar");
                setIsVerifying(false);
                setScanStatus('FAILED');
                setQrCodeData(null);
                return;
            }
            
            const newRetryCount = retryCount + 1;
            setRetryCount(newRetryCount);
            
            if (newRetryCount >= 3) {
                setShowMaxAttemptsWarning(true);
                toast.error(`${errorMessage} Maximum attempts reached. Please try alternate verification method.`, {
                    position: "top-center",
                    autoClose: 5000,
                });
            } else {
                toast.error(`${errorMessage} You have ${3 - newRetryCount} attempt(s) remaining.`, {
                    position: "top-center",
                });
            }
            
            errorLogger(error?.response?.data || error, "Face Auth Enrol");
            setIsVerifying(false);
            setScanStatus('FAILED');
            setQrCodeData(null);
        }
    };

    const performScanStatusCheck = async (currentTxnId: string) => {
        if (!isPollingActiveRef.current) {
            return;
        }

        try {
            const response = await postDataJSON<any>(
                endpoints.faceAuthCapturePID,
                {
                    txnId: currentTxnId
                }
            );

            if (response.data && response.data.status) {
                const status = response.data.status;
                setScanStatus(status);

                if (status === 'COMPLETE') {
                    isPollingActiveRef.current = false;
                    if (scanStatusIntervalRef.current) {
                        clearInterval(scanStatusIntervalRef.current);
                        scanStatusIntervalRef.current = null;
                    }

                    const apiResponseTrxnId = response.data.apiResponseTrxnId?.api_response_trxn_id;
                    if (apiResponseTrxnId) {
                        // COMMENTED OUT: Only creation flow is active
                        // if (beneficiary?.beneficiaryAuthType === 'Face_Auth_Creation') {
                            await enrollAbhaByFaceAuth(currentTxnId, apiResponseTrxnId);
                        // } else {
                        //     await verifyFaceAuthentication(currentTxnId, apiResponseTrxnId);
                        // }
                    } else {
                        toast.error('Invalid response from face authentication', {
                            position: "top-center",
                        });
                    }
                } else if (status === 'FAILED') {
                    isPollingActiveRef.current = false;
                    if (scanStatusIntervalRef.current) {
                        clearInterval(scanStatusIntervalRef.current);
                        scanStatusIntervalRef.current = null;
                    }
                    
                    const newRetryCount = retryCount + 1;
                    setRetryCount(newRetryCount);
                    
                    if (newRetryCount >= 3) {
                        setShowMaxAttemptsWarning(true);
                        toast.error('Face authentication failed. Maximum attempts reached. Please try alternate verification method.', {
                            position: "top-center",
                            autoClose: 5000,
                        });
                    } else {
                        toast.error(`Face authentication failed. You have ${3 - newRetryCount} attempt(s) remaining.`, {
                            position: "top-center",
                        });
                    }
                }
            }
        } catch (error: any) {
            errorLogger(error?.response?.data || error?.response || error);
        }
    };

    const startPolling = (currentTxnId: string) => {
        if (scanStatusIntervalRef.current) {
            clearInterval(scanStatusIntervalRef.current);
            scanStatusIntervalRef.current = null;
        }

        isPollingActiveRef.current = true;

        scanStatusIntervalRef.current = setInterval(() => {
            performScanStatusCheck(currentTxnId);
        }, 10000);
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleRegenerateQR = async () => {
        if (retryCount >= 3) {
            toast.error('Maximum attempts reached. Please try alternate verification method.', {
                position: "top-center",
            });
            return;
        }
        
        setQrCodeData(null);
        setScanStatus('');
        setQrId('');
        setTxnId('');
        setIsGenerating(true);
        setInvalidAadhaarError(null);
        setBiometricMismatchError(null);
        setInvalidGenderMessage('');
        
        try {
            let newTxnId = '';
            let newDeepLink = '';
            
            if (beneficiary?.beneficiaryAuthType === 'Face_Auth_Creation') {
                const initResponse = await postDataJSON<any>(
                    `${endpoints.faceAuthInit}`,
                    {}
                );
                
                if (initResponse.data?.txnId && initResponse.data?.faceAuthDeepLink) {
                    newTxnId = initResponse.data.txnId;
                    newDeepLink = initResponse.data.faceAuthDeepLink;
                    dispatch(setTsnID(newTxnId));
                } else {
                    toast.error('Failed to regenerate QR code. Please try again.', {
                        position: "top-center",
                    });
                    setIsGenerating(false);
                    return;
                }
            } else {
                const originalSearchTxnId = sessionStorage.getItem('faceAuthSearchTxnId');
                const beneficiaryIndex = sessionStorage.getItem('faceAuthBeneficiaryIndex');
                
                if (!beneficiaryIndex || !originalSearchTxnId) {
                    toast.error('Missing required data. Please go back and select the beneficiary again.', {
                        position: "top-center",
                    });
                    setIsGenerating(false);
                    return;
                }
                
                const requestOtpPayload = {
                    index: beneficiaryIndex,
                    txnId: originalSearchTxnId
                };
                
                const response = await postDataJSON<any>(`${endpoints.faceAuthRequestOtp}`, requestOtpPayload);
                
                newTxnId = response.data?.txnId || response.data?.txn_id;
                newDeepLink = response.data?.faceAuthDeepLink;
                
                if (newTxnId && newDeepLink) {
                    dispatch(setTsnID(newTxnId));
                    sessionStorage.setItem('faceAuthDeepLink', newDeepLink);
                } else {
                    toast.error('Failed to regenerate QR code. Please try again.', {
                        position: "top-center",
                    });
                    setIsGenerating(false);
                    return;
                }
            }
            
            const encoded_qr_code_data = encodeWithKeyAndShift(newDeepLink);
            const qrResponse = await postDataJSON<any>(`${endpoints.getQrCode}`, {
                mcp_qr_code_data: encoded_qr_code_data,
            });
            
            setQrCodeData(qrResponse.data);
            setQrId(`qr_${Date.now()}`);
            setTxnId(newTxnId);
            setScanStatus('not_scanned');
            setTimeLeft(600);
            setIsGenerating(false);
            
            toast.success(`New QR Code generated successfully!`, {
                position: "top-center",
                autoClose: 3000,
            });
            
            startPolling(newTxnId);
        } catch (error: any) {
            toast.error('Failed to regenerate QR code. Please try again.', {
                position: "top-center",
            });
            errorLogger(error?.response?.data || error?.response || error);
            setIsGenerating(false);
        }
    };

    const handleGenerateQR = async () => {
        setIsGenerating(true);
        setInvalidAadhaarError(null); 
        setBiometricMismatchError(null);
        try {
            let txnIdToUse = beneficiary?.tsnId;
            let deepLinkToUse = '';
            
            if (beneficiary?.beneficiaryAuthType === 'Face_Auth_Creation') {
                try {
                    const initResponse = await postDataJSON<any>(
                        `${endpoints.faceAuthInit}`,
                        {}
                    );
                    
                    if (initResponse.data?.txnId && initResponse.data?.faceAuthDeepLink) {
                        txnIdToUse = initResponse.data.txnId;
                        deepLinkToUse = initResponse.data.faceAuthDeepLink;
                        dispatch(setTsnID(txnIdToUse));
                    } else {
                        toast.error(initResponse.data?.message || 'Failed to initiate face authentication', {
                            position: "top-center",
                        });
                        setIsGenerating(false);
                        return;
                    }
                } catch (error: any) {
                    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to initiate face authentication';
                    toast.error(errorMessage, {
                        position: "top-center",
                    });
                    errorLogger(error?.response?.data || error, "Face Auth Init");
                    setIsGenerating(false);
                    return;
                }
            } else {
                deepLinkToUse = sessionStorage.getItem('faceAuthDeepLink') || '';
                
                if (!deepLinkToUse) {
                    toast.error('Deep link not found. Please try again from the beginning.', {
                        position: "top-center",
                    });
                    setIsGenerating(false);
                    return;
                }
            }
            
            if (!txnIdToUse) {
                toast.error('Transaction ID not found. Please try again.', {
                    position: "top-center",
                });
                setIsGenerating(false);
                return;
            }
            
            if (!deepLinkToUse) {
                toast.error('Deep link not found. Please try again.', {
                    position: "top-center",
                });
                setIsGenerating(false);
                return;
            }
            
            const encoded_qr_code_data = encodeWithKeyAndShift(deepLinkToUse);
            const response = await postDataJSON<any>(`${endpoints.getQrCode}`, {
                mcp_qr_code_data: encoded_qr_code_data,
            });
            
            setQrCodeData(response.data);
            setQrId(`qr_${Date.now()}`);
            setTxnId(txnIdToUse);
            setScanStatus('not_scanned');
            setTimeLeft(600);
            setIsGenerating(false);
            
            toast.success(`QR Code generated successfully!`, {
                position: "top-center",
                autoClose: 3000,
            });

            startPolling(txnIdToUse);
        } catch (error: any) {
            errorLogger(error?.response?.data || error?.response || error);
            setIsGenerating(false);
        }
    };

    const handleBackClick = () => {
        isPollingActiveRef.current = false;
        if (scanStatusIntervalRef.current) {
            clearInterval(scanStatusIntervalRef.current);
            scanStatusIntervalRef.current = null;
        }
        sessionStorage.removeItem('faceAuthDeepLink');
        sessionStorage.removeItem('faceAuthBeneficiaryIndex');
        sessionStorage.removeItem('faceAuthSearchTxnId');
        handleClose('back');
    };

    const handleDiscardClick = () => {
        isPollingActiveRef.current = false;
        if (scanStatusIntervalRef.current) {
            clearInterval(scanStatusIntervalRef.current);
            scanStatusIntervalRef.current = null;
        }
        sessionStorage.removeItem('faceAuthDeepLink');
        sessionStorage.removeItem('faceAuthBeneficiaryIndex');
        sessionStorage.removeItem('faceAuthSearchTxnId');
        handleClose('close');
    };

    return (
        <div className="modal_face_authentication">
            <Modal
                show={showModal}
                onHide={() => handleClose('close')}
                dialogClassName={'face-auth-qr-modal'}
                centered={true}
                backdrop="static"
                size="lg"
            >
                <Modal.Header>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <div className="d-flex align-items-center beneficiary_back_button" onClick={handleBackClick}>
                            <i className="bi bi-arrow-left" style={{ cursor: "pointer" }}></i>
                            <span className="ms-1">Back</span>
                        </div>
                        <div className="d-flex align-items-center beneficiary_discard_button" onClick={handleDiscardClick}>
                            <span className="me-0">Discard</span>
                            <i className="bi bi-x"></i>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    {!invalidGenderMessage && (
                        <div style={{ display: 'flex', gap: '30px', padding: '20px' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ marginBottom: '20px' }}>
                                    <h6 style={{ color: '#28a745', marginBottom: '10px' }}>
                                        <i className="bi bi-check-circle" style={{ marginRight: '8px' }}></i>
                                        Prerequisites:
                                    </h6>
                                <div style={{ marginLeft: '10px' }}>
                                    <p style={{ fontSize: '14px', marginBottom: '8px' }}>1. Download ABHA App from</p>
                                    <div style={{ display: 'flex', gap: '10px', marginLeft: '20px', marginBottom: '10px' }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <a href="https://play.google.com/store/apps/details?id=in.ndhm.phr" target="_blank" rel="noopener noreferrer">
                                                <img 
                                                    src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=https://play.google.com/store/apps/details?id=in.ndhm.phr" 
                                                    alt="Google Play QR Code" 
                                                    style={{ width: '80px', height: '80px', border: '1px solid #ddd' }} 
                                                />
                                            </a>
                                            <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                                <i className="bi bi-google-play" style={{ color: '#34A853', fontSize: '16px' }}></i>
                                                <span style={{ fontSize: '11px' }}>Google Play</span>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <a href="https://apps.apple.com/in/app/abha-abdm/id1630917266" target="_blank" rel="noopener noreferrer">
                                                <img 
                                                    src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=https://apps.apple.com/in/app/abha-abdm/id1630917266" 
                                                    alt="iOS App Store QR Code" 
                                                    style={{ width: '80px', height: '80px', border: '1px solid #ddd' }} 
                                                />
                                            </a>
                                            <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                                <i className="bi bi-apple" style={{ color: '#000000', fontSize: '16px' }}></i>
                                                <span style={{ fontSize: '11px' }}>App Store</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h6 style={{ color: '#28a745', marginBottom: '10px' }}>
                                    <i className="bi bi-check-circle" style={{ marginRight: '8px' }}></i>
                                    Steps to Follow:
                                </h6>
                                <ol style={{ fontSize: '12px', paddingLeft: '20px', lineHeight: '1.8' }}>
                                    <li>Select Generate QR Code on the right pane</li>
                                    <li>Open ABHA App → Tap on the Portal FaceAuth icon (top left)</li>
                                    <li>Scan the QR code using the ABHA App</li>
                                    <li>If Aadhaar RD Service is missing, app redirects for installation from Play Store/App Store</li>
                                    <li>Aadhaar RD Service captures face for authentication → Successful confirmation on phone</li>
                                    <li>Success prompt appears on ABHA Portal → Proceed with ABHA creation</li>
                                </ol>
                            </div>
                        </div>

                        <div style={{ flex: '0 0 300px', display: 'flex', flexDirection: 'column', alignItems: 'center', borderLeft: '1px solid #e0e0e0', paddingLeft: '30px' }}>
                            <div style={{ 
                                width: '250px', 
                                height: '250px', 
                                border: '2px solid #ddd', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                backgroundColor: '#f8f9fa',
                                position: 'relative'
                            }}>
                                {scanStatus === 'FAILED' && !showMaxAttemptsWarning && retryCount < 3 ? (
                                    <div style={{ textAlign: 'center' }}>
                                        <Button
                                            onClick={handleRegenerateQR}
                                            buttonName={'Re-generate QR Code'}
                                            colorType={'primary'}
                                            id={'regenerate-qr-btn'}
                                            icon={''}
                                            iconPosition={''}
                                            disabled={isGenerating}
                                        />
                                        <p style={{ fontSize: '12px', color: '#ff6b6b', marginTop: '8px', textAlign: 'center' }}>
                                            Attempts remaining: {3 - retryCount}
                                        </p>
                                    </div>
                                ) : qrCodeData && !showMaxAttemptsWarning && retryCount < 3 ? (
                                    <div style={{ textAlign: 'center', padding: '10px' }}>
                                        <img 
                                            src={qrCodeData} 
                                            alt="Face Authentication QR Code"
                                            style={{
                                                width: '230px',
                                                height: '230px',
                                                border: '1px solid #ccc',
                                                borderRadius: '8px'
                                            }}
                                        />
                                    </div>
                                ) : isGenerating ? (
                                    <div style={{ textAlign: 'center' }}>
                                        <div className="spinner-border text-primary" role="status" style={{ marginBottom: '10px' }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p style={{ fontSize: '14px', color: '#666' }}>Generating QR Code...</p>
                                    </div>
                                ) : (
                                    <Button
                                        onClick={handleGenerateQR}
                                        buttonName={'Generate QR'}
                                        colorType={'primary'}
                                        id={'generate-qr-btn'}
                                        icon={''}
                                        iconPosition={''}
                                        disabled={retryCount >= 3 || showMaxAttemptsWarning}
                                    />
                                )}
                            </div>
                            
                            <div style={{ marginTop: '15px', textAlign: 'center', width: '100%' }}>
                                <p style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                                    Timer will start once QR code will be displayed
                                </p>
                                {qrCodeData && (
                                    <>
                                        <div style={{ 
                                            fontSize: '24px', 
                                            fontWeight: 'bold', 
                                            color: timeLeft < 60 ? '#dc3545' : '#007bff',
                                            marginBottom: '5px'
                                        }}>
                                            {formatTime(timeLeft)}
                                        </div>
                                        <span style={{ 
                                            color: timeLeft <= 60 ? '#dc3545' : '#ff6b6b',
                                            fontSize: '12px',
                                            display: 'block',
                                            marginBottom: '10px'
                                        }}>
                                            {timeLeft <= 60 ? 'Expires soon!' : 'Valid for 10 minutes'}
                                        </span>
                                        {(scanStatus || isVerifying) && (
                                            <div style={{
                                                marginTop: '10px',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                backgroundColor: scanStatus === 'COMPLETE' ? '#d4edda' : scanStatus === 'FAILED' ? '#f8d7da' : '#fff3cd'
                                            }}>
                                                <span style={{
                                                    color: scanStatus === 'COMPLETE' ? '#28a745' : scanStatus === 'FAILED' ? '#dc3545' : '#ffc107',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px'
                                                }}>
                                                    {isVerifying ? (
                                                        <>
                                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                            Verifying authentication...
                                                        </>
                                                    ) : (
                                                        <>Status: {scanStatus === 'COMPLETE' ? '✅ Verification Completed!' : scanStatus === 'FAILED' ? '❌ Authentication Failed!' : '⏳ Pending verification...'}</>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        
                                        {timeLeft === 0 && (
                                            <p style={{ color: '#dc3545', fontSize: '14px', marginTop: '10px', fontWeight: 'bold' }}>
                                                ❌ QR Code expired. Please generate again.
                                            </p>
                                        )}
                                    </>
                                )}
                                
                                {showMaxAttemptsWarning && (
                                    <div style={{ 
                                        marginTop: '15px', 
                                        padding: '12px', 
                                        backgroundColor: '#fff3cd', 
                                        borderRadius: '6px',
                                        border: '1px solid #ffc107'
                                    }}>
                                        <p style={{ 
                                            color: '#856404', 
                                            fontSize: '13px', 
                                            fontWeight: 'bold',
                                            marginBottom: '8px'
                                        }}>
                                            ⚠️ Maximum Attempts Reached
                                        </p>
                                        <p style={{ 
                                            color: '#856404', 
                                            fontSize: '12px',
                                            margin: 0
                                        }}>
                                            Please try an alternate verification method.
                                        </p>
                                    </div>
                                )}
                                
                                {mobileLinkedError && (
                                    <div style={{ 
                                        marginTop: '15px', 
                                        padding: '12px', 
                                        backgroundColor: '#fff3cd', 
                                        borderRadius: '6px',
                                        border: '1px solid #ffc107'
                                    }}>
                                        <p style={{ 
                                            color: '#856404', 
                                            fontSize: '13px', 
                                            fontWeight: 'bold',
                                            marginBottom: '8px'
                                        }}>
                                            ⚠️ {mobileLinkedError.code}
                                        </p>
                                        <p style={{ 
                                            color: '#856404', 
                                            fontSize: '12px',
                                            margin: 0
                                        }}>
                                            {mobileLinkedError.message}
                                        </p>
                                    </div>
                                )}
                                
                                {invalidAadhaarError && (
                                    <div style={{ 
                                        marginTop: '15px', 
                                        padding: '12px', 
                                        backgroundColor: '#fff3cd', 
                                        borderRadius: '6px',
                                        border: '1px solid #ffc107'
                                    }}>
                                        <p style={{ 
                                            color: '#856404', 
                                            fontSize: '13px', 
                                            fontWeight: 'bold',
                                            marginBottom: '8px'
                                        }}>
                                            ⚠️ Invalid Aadhaar Number
                                        </p>
                                        <p style={{ 
                                            color: '#856404', 
                                            fontSize: '12px',
                                            margin: 0
                                        }}>
                                            {invalidAadhaarError}. Please enter the correct Aadhaar number and regenerate QR code.
                                        </p>
                                    </div>
                                )}
                                
                                {biometricMismatchError && (
                                    <div style={{ 
                                        marginTop: '15px', 
                                        padding: '12px', 
                                        backgroundColor: '#fff3cd', 
                                        borderRadius: '6px',
                                        border: '1px solid #ffc107'
                                    }}>
                                        <p style={{ 
                                            color: '#856404', 
                                            fontSize: '13px', 
                                            fontWeight: 'bold',
                                            marginBottom: '8px'
                                        }}>
                                            ⚠️ {biometricMismatchError.code}
                                        </p>
                                        <p style={{ 
                                            color: '#856404', 
                                            fontSize: '12px',
                                            margin: 0
                                        }}>
                                            {biometricMismatchError.message}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    )}
                    
                    {invalidGenderMessage && (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '16px', marginBottom: '20px' }}>{invalidGenderMessage}</p>
                            <Button
                                onClick={() => handleClose('close')}
                                disabled={false}
                                id="gender-mismatch-close-btn"
                                buttonName={'Close'}
                                colorType={'primary'}
                                icon={''}
                                iconPosition={''}
                            />
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default FaceAuthenticationModal;